/*
  <mv-sticky-atc> — the product page's sticky add-to-cart bar (blocks/product_sticky-atc.liquid).

  Attributes:
    data-section-id        the product section (its <mv-product> controller drives the bar)
    data-display-mode      always | after_scroll | after_pixels | after_percent
    data-display-threshold px (after_pixels) or % (after_percent)
    data-main-form         id of the section's main product form (Dawn <product-form>)
    data-fallback-form     id of the bar's own form, used when the main form is not on the page
    data-scroll-target     "#ProductInfo-id" | "#variant-selects-id" | "#quantity-breaks-id" |
                           ".pricing-table" — present when the button scrolls instead of adding

  Behaviour:
  - Visibility is re-evaluated on scroll / resize (rAF-coalesced): `mv-sticky-atc--visible` plus
    `--sticky-atc-offset` on :root (bar height while visible, 0px otherwise; the scroll-to-top
    button and bottom progress bar read it). In the theme editor the bar is forced visible while
    its block is selected.
  - after_scroll: visible once the main form's submit button (fallback: the product info column)
    has scrolled out above the viewport — the same moment the regular button stops being usable.
  - Add to cart: the button owns the main form (`form=` attribute) so Dawn's product-form.js does
    the POST and opens the cart drawer / notification; the bar mirrors the main button's loading
    state and shows cart errors from Dawn's pubsub in its own line. Without a main form the
    button owns the bar's fallback form; the current quantity is injected on `formdata`.
  - Variant changes: the controller swaps the bar's data-mv-refresh parts; this element only
    updates the button's disabled state and reads the label back. The bar's pickers dispatch
    `mv:variant-select` (combined) / `mv:option-change` (separate) to the controller.

  <mv-scroll-buttons> — the "Scroll-to buttons" block (blocks/product_scroll-buttons.liquid).
  Its links carry data-mv-scroll-to (#<section id>) and data-mv-scroll-alt (#shopify-section-…);
  a click smooth-scrolls the first one that exists (or the JSON-template section whose wrapper id
  ends in "__<key>") to just under whatever is stuck at the top.
  Both elements share scrollToElement().
*/
(() => {
  window.Multiverse = window.Multiverse || {};
  const MV = window.Multiverse;
  const ERROR_MS = 4000;
  // Dawn declares PUB_SUB_EVENTS as a top-level `const` (assets/constants.js): a global lexical
  // binding, NOT a window property, so it has to be reached by name, guarded with typeof.
  const dawnEvents = () => {
    try {
      return typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS || null;
    } catch (e) {
      return null;
    }
  };
  const STUCK_SELECTOR = '.section-header.mv-header-sticky, .announcement-bar-section--sticky';

  const scrollToElement = (element, gap) => {
    let stuck = 0;
    document.querySelectorAll(STUCK_SELECTOR).forEach((el) => {
      stuck = Math.max(stuck, el.getBoundingClientRect().bottom);
    });
    const top = element.getBoundingClientRect().top + window.scrollY - stuck - (gap || 0);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? 'auto' : 'smooth' });
  };

  class MvStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.mode = this.dataset.displayMode || 'after_scroll';
      this.threshold = parseFloat(this.dataset.displayThreshold) || 0;
      this.button = this.querySelector('.mv-sticky-atc__button');
      this.errorLine = this.querySelector('.mv-sticky-atc__error');
      this.visible = this.classList.contains('mv-sticky-atc--visible');
      this.forced = false;
      this.frame = 0;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.onVariantChange = (event) => {
        if (event.detail.sectionId !== this.sectionId) return;
        this.applyVariant(event.detail.variant);
      };
      this.onQuantityChange = (event) => {
        if (event.detail.sectionId !== this.sectionId) return;
        this.quantity = event.detail.quantity;
      };
      this.onChange = (event) => {
        const target = event.target;
        if (target instanceof HTMLSelectElement && target.hasAttribute('data-mv-option-select')) {
          this.dispatchOptionChange();
        }
      };
      this.onClick = (event) => {
        if (this.dataset.scrollTarget && event.target.closest('.mv-sticky-atc__button')) {
          event.preventDefault();
          this.scrollToTarget(this.dataset.scrollTarget);
        }
      };
      this.onBlockSelect = (event) => {
        if (!this.contains(event.target) && event.target !== this) return;
        this.forced = true;
        this.update();
      };
      this.onBlockDeselect = (event) => {
        if (!this.contains(event.target) && event.target !== this) return;
        this.forced = false;
        this.update();
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      document.addEventListener('mv:variant-change', this.onVariantChange);
      document.addEventListener('mv:quantity-change', this.onQuantityChange);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.resizeObserver = new ResizeObserver(() => this.publishOffset());
      this.resizeObserver.observe(this);

      this.wireForm();
      const state = MV.product && MV.product.getState ? MV.product.getState(this.sectionId) : null;
      this.quantity = state ? state.quantity : 1;
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('mv:variant-change', this.onVariantChange);
      document.removeEventListener('mv:quantity-change', this.onQuantityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mirror) this.mirror.disconnect();
      if (this.unsubscribeError) this.unsubscribeError();
      document.documentElement.style.removeProperty('--sticky-atc-offset');
    }

    // ---- visibility ----
    get anchor() {
      const mainForm = document.getElementById(this.dataset.mainForm);
      const submit = mainForm ? mainForm.querySelector('[type="submit"]') : null;
      const ownButton = mainForm && mainForm.closest('mv-sticky-atc') === this;
      if (submit && !ownButton) return submit;
      return (
        document.getElementById(`ProductInfo-${this.sectionId}`) ||
        (MV.product && MV.product.get ? MV.product.get(this.sectionId) : null)
      );
    }

    shouldShow() {
      if (this.forced) return true;
      const scrollY = MV.clampScrollY ? MV.clampScrollY() : window.scrollY;
      switch (this.mode) {
        case 'always':
          return true;
        case 'after_pixels':
          return scrollY >= this.threshold;
        case 'after_percent': {
          const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
          const percent = max ? (scrollY / max) * 100 : 0;
          return percent >= this.threshold;
        }
        default: {
          const anchor = this.anchor;
          if (!anchor) return scrollY > 0;
          return anchor.getBoundingClientRect().bottom < 0;
        }
      }
    }

    update() {
      const next = this.shouldShow();
      if (next !== this.visible) {
        this.visible = next;
        this.classList.toggle('mv-sticky-atc--visible', next);
        this.setAttribute('aria-hidden', next ? 'false' : 'true');
      }
      this.publishOffset();
    }

    publishOffset() {
      const offset = this.visible ? this.offsetHeight : 0;
      if (offset === this.lastOffset) return;
      this.lastOffset = offset;
      document.documentElement.style.setProperty('--sticky-atc-offset', `${offset}px`);
    }

    // ---- add to cart ----
    wireForm() {
      if (!this.button || this.dataset.scrollTarget) return;
      const mainForm = document.getElementById(this.dataset.mainForm);
      const fallback = document.getElementById(this.dataset.fallbackForm);
      const usesMain = !!(mainForm && mainForm.closest('mv-sticky-atc') !== this);
      const form = usesMain ? mainForm : fallback;
      if (!form) return;
      this.button.setAttribute('form', form.getAttribute('id'));

      if (usesMain) {
        // Dawn's product-form.js runs the request on the main form and animates ITS button;
        // mirror that button's loading state onto ours.
        const mainButton = form.closest('product-form')
          ? form.closest('product-form').querySelector('[type="submit"]')
          : form.querySelector('[type="submit"]');
        if (mainButton) {
          this.mirror = new MutationObserver(() => {
            const loading = mainButton.classList.contains('loading');
            this.button.classList.toggle('loading', loading);
            this.button.toggleAttribute('aria-busy', loading);
          });
          this.mirror.observe(mainButton, { attributes: true, attributeFilter: ['class'] });
        }
      } else {
        // Our own form carries no quantity field: inject the controller's quantity at submit time.
        form.addEventListener('formdata', (event) => {
          event.formData.set('quantity', String(this.quantity || 1));
        });
      }

      const events = dawnEvents();
      if (typeof subscribe === 'function' && events && events.cartError) {
        this.unsubscribeError = subscribe(events.cartError, (payload) => {
          if (!payload || !this.visible) return;
          const text =
            typeof payload.errors === 'string'
              ? payload.errors
              : Array.isArray(payload.errors)
                ? payload.errors.join(', ')
                : payload.message || (window.cartStrings && cartStrings.error) || '';
          this.showError(text);
        });
      }
    }

    showError(text) {
      if (!this.errorLine || !text) return;
      this.errorLine.textContent = text;
      this.errorLine.hidden = false;
      window.clearTimeout(this.errorTimer);
      this.errorTimer = window.setTimeout(() => {
        this.errorLine.hidden = true;
      }, ERROR_MS);
    }

    applyVariant(variant) {
      if (!this.button || this.dataset.scrollTarget) return;
      const soldOut = !variant || !variant.available;
      this.button.toggleAttribute('disabled', soldOut);
    }

    // ---- pickers ----
    dispatchOptionChange() {
      const selects = [...this.querySelectorAll('select[data-mv-option-select]')].sort(
        (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      );
      const optionValues = selects.map((select) => select.value);
      this.dispatchEvent(new CustomEvent('mv:option-change', { bubbles: true, detail: { optionValues } }));
    }

    // ---- scroll function ----
    // The option values are the reference theme's selectors; each maps to the Multiverse block that plays
    // that role (B1 variant picker, B2 quantity breaks / pricing table), then to the info column.
    scrollToTarget(target) {
      const sid = this.sectionId;
      const candidates = [target.replace(/-id$/, `-${sid}`)];
      if (target.startsWith('#variant-selects')) candidates.push('.mv-variant-picker', 'variant-selects');
      if (target.startsWith('#quantity-breaks')) candidates.push('mv-quantity-breaks', '.mv-qb');
      if (target === '.pricing-table') candidates.push('.mv-pricing-table', '[class*="pricing-table"]', 'mv-quantity-breaks');
      candidates.push(`#ProductInfo-${sid}`);
      const section = MV.product && MV.product.get ? MV.product.get(sid) : null;
      let element = null;
      for (const selector of candidates) {
        try {
          element = (section && section.querySelector(selector)) || document.querySelector(selector);
        } catch (e) {
          element = null;
        }
        if (element) break;
      }
      if (!element) return;
      scrollToElement(element, 16);
    }
  }

  // ---- mv-scroll-buttons ----
  class MvScrollButtons extends HTMLElement {
    connectedCallback() {
      this.onClick = (event) => {
        const link = event.target.closest('[data-mv-scroll-to]');
        if (!link || !this.contains(link)) return;
        const target = MvScrollButtons.resolve(link.dataset.mvScrollTo, link.dataset.mvScrollAlt);
        if (!target) return;
        event.preventDefault();
        scrollToElement(target, 12);
      };
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    // The merchant pastes what the section badge copies (#shopify-section-<section.id>); the bare
    // section id also works, and so does the short key of a JSON-template section (`disclosures`
    // for a wrapper whose id is shopify-section-template--123__disclosures).
    static resolve(hash, alt) {
      const key = (hash || '').replace(/^#/, '').replace(/^shopify-section-/, '');
      if (!key) return null;
      const selectors = [hash, alt, `.shopify-section[id$="__${key}"]`];
      for (const selector of selectors) {
        try {
          const found = selector && document.querySelector(selector);
          if (found) return found;
        } catch (e) {
          /* not a valid selector: keep looking */
        }
      }
      return null;
    }
  }

  const define = (name, cls) => {
    if (MV.defineOnce) MV.defineOnce(name, cls);
    else if (!customElements.get(name)) customElements.define(name, cls);
  };
  define('mv-sticky-atc', MvStickyAtc);
  define('mv-scroll-buttons', MvScrollButtons);
})();
